import { useCallback, useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ClipboardPenLine, GripHorizontal, Headphones, Loader2, Star } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils/cn';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { API_CALL_QUALITY_FORM, API_CALL_QUALITY_PENDING, API_CALL_QUALITY_SKIP, API_CALL_QUALITY_SUBMIT } from '@/service/url';
import { useCallStore } from '@/router/zustand/callStore';
import { formatDateTime } from '@/utilities/helpers';

import { canCallQualityFeedback, type CallQualityForm } from './feedback';

interface Pending {
  id: string | number;
  trace_id: string | null;
  call_sid: string | null;
  customer_number: string | null;
  created_at: string;
}

const POS_KEY = 'callAudioFeedbackTrayPos';

function readPos(): { x: number; y: number } | null {
  try {
    const p = JSON.parse(localStorage.getItem(POS_KEY) || 'null');
    return p && Number.isFinite(p.x) && Number.isFinite(p.y) ? p : null;
  } catch {
    return null;
  }
}

function useDefaultAnchor(active: boolean) {
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null);
  useEffect(() => {
    const el = active ? document.querySelector('.cti-live-stack') : null;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setAnchor({ top: Math.max(8, r.top + 12), right: Math.max(8, window.innerWidth - r.right + 12) });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [active]);
  return anchor;
}

const isWebrtcLogin = () => {
  try {
    return (sessionStorage.getItem('activeLoginType') || '').trim().toUpperCase() === 'WEBRTC';
  } catch {
    return false;
  }
};

function FeedbackCard({ item, form, onDone }: { item: Pending; form: CallQualityForm; onDone: () => void }) {
  const [rating, setRating] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [picked, setPicked] = useState<Record<string, string[]>>({});
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const showIssues = rating != null && rating <= form.threshold && form.sections.length > 0;
  const missing =
    (form.rating_required && rating == null) ||
    (showIssues && form.sections.some((s) => s.required && !(picked[s.key] || []).length)) ||
    (form.comment_enabled && form.comment_required && !comment.trim());

  const toggle = (section: string, option: string) =>
    setPicked((p) => {
      const cur = p[section] || [];
      return { ...p, [section]: cur.includes(option) ? cur.filter((o) => o !== option) : [...cur, option] };
    });

  const send = async (skip: boolean) => {
    setBusy(true);
    try {
      if (skip) await postApiCall(API_CALL_QUALITY_SKIP.replace(':id', String(item.id)), {});
      else
        await postApiCall(API_CALL_QUALITY_SUBMIT.replace(':id', String(item.id)), {
          rating,
          issues: showIssues ? picked : {},
          comment: form.comment_enabled ? comment : '',
        });
      onDone();
    } catch (e) {
      // Already handled in another tab / expired: drop it; other errors keep the card.
      if (String(e).includes('already submitted')) onDone();
      else setBusy(false);
    }
  };

  const shown = hover ?? rating;
  return (
    <div className="space-y-3 p-3 text-sm">
      <div className="text-xs text-muted-foreground">
        {item.customer_number ? `Call with ${item.customer_number}` : 'Recent call'} · {formatDateTime(item.created_at)}
      </div>
      <div className="break-all font-mono text-[11px] text-muted-foreground">Call ID: {item.call_sid || '-'}</div>
      <div>
        <div className="mb-1 font-medium">
          {form.rating_question} {form.rating_required && <span className="text-destructive">*</span>}
        </div>
        <div className="flex gap-1" onMouseLeave={() => setHover(null)}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              aria-label={`${n} star${n > 1 ? 's' : ''}`}
              aria-pressed={rating === n}
              onMouseEnter={() => setHover(n)}
              onClick={() => setRating(n)}
              className="rounded p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Star className={cn('h-7 w-7', shown != null && n <= shown ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/50')} />
            </button>
          ))}
        </div>
      </div>

      {showIssues && (
        <div className="space-y-2">
          <div className="font-medium">What went wrong?</div>
          {form.sections.map((s) => (
            <div key={s.key}>
              <div className="mb-1 text-xs text-muted-foreground">
                {s.title} {s.required && <span className="text-destructive">*</span>}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {s.options.map((o) => {
                  const on = (picked[s.key] || []).includes(o.key);
                  return (
                    <button
                      key={o.key}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggle(s.key, o.key)}
                      className={cn(
                        'rounded-full border px-2.5 py-1 text-xs transition-colors',
                        on ? 'border-amber-500 bg-amber-500/15 text-foreground' : 'hover:bg-muted',
                      )}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {form.comment_enabled && (
        <Textarea
          aria-label="Comment"
          rows={2}
          maxLength={1000}
          placeholder={form.comment_required ? 'Comment (required)' : 'Comment (optional)'}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      )}

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => send(true)}>
          Skip
        </Button>
        <Button size="sm" disabled={busy || missing} onClick={() => send(false)}>
          {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Submit
        </Button>
      </div>
    </div>
  );
}

export default function CallQualityFeedbackTray() {
  const enabled = canCallQualityFeedback('add');
  const [form, setForm] = useState<CallQualityForm | null>(null);
  const [items, setItems] = useState<Pending[]>([]);
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [pos, setPos] = useState(readPos);
  const boxRef = useRef<HTMLDivElement>(null);
  const draggedRef = useRef(false);
  const countRef = useRef(0);
  const queuedSidRef = useRef<string | null>(null);
  const completedCall = useCallStore((s: any) => s.completedCallToPersist);
  const anchor = useDefaultAnchor(enabled && !!form && items.length > 0 && !pos);

  const refresh = useCallback(async (openOnNew: boolean) => {
    try {
      const [f, pending] = await Promise.all([
        getApiCall(API_CALL_QUALITY_FORM, undefined),
        getApiCall(API_CALL_QUALITY_PENDING, undefined),
      ]);
      const list: Pending[] = f && Array.isArray(pending) ? pending : [];
      setForm(f || null);
      setItems(list);
      if (openOnNew && list.length > countRef.current) {
        setOpen(true);
        setIdx(0);
      }
      countRef.current = list.length;
    } catch {
      /* optional feature: stay quiet */
    }
  }, []);

  useEffect(() => {
    if (enabled) refresh(false);
  }, [enabled, refresh]);

  // Call over: queue a card for it (answered WebRTC calls only), then refetch the stack.
  useEffect(() => {
    if (!enabled || completedCall?.answer_status !== 'answered' || !isWebrtcLogin()) return;
    const s: any = useCallStore.getState();
    // Same call id the call record is saved with (AgentPortalPage persist effect).
    const callSid = s.sipHeaders?.['call-id'] || completedCall.call_sid || null;
    if (!callSid || queuedSidRef.current === callSid) return;
    queuedSidRef.current = callSid;
    const inbound = ['incoming', 'inbound', 'in'].includes(String(completedCall.direction || s.direction || '').toLowerCase());
    postApiCall(API_CALL_QUALITY_PENDING, {
      call_sid: callSid,
      trace_id: completedCall.trace_id || null,
      customer_number: completedCall.customer_number || (inbound ? completedCall.caller_number : completedCall.callee_number) || null,
    })
      .then(() => refresh(true))
      .catch(() => {
        /* optional feature: never bother the agent */
      });
  }, [enabled, completedCall, refresh]);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el || !pos) return;
    const r = el.getBoundingClientRect();
    const x = Math.min(Math.max(8, r.left), window.innerWidth - r.width - 8);
    const y = Math.min(Math.max(8, r.top), window.innerHeight - r.height - 8);
    if (Math.abs(x - r.left) > 0.5 || Math.abs(y - r.top) > 0.5) setPos({ x, y });
  });

  if (!enabled || !form || !items.length) return null;
  const cur = Math.min(idx, items.length - 1);

  const done = (id: Pending['id']) => {
    setItems((l) => l.filter((i) => i.id !== id));
    countRef.current = Math.max(0, countRef.current - 1);
  };

  const startDrag = (e: ReactPointerEvent) => {
    if (e.button !== 0 || !boxRef.current) return;
    const r = boxRef.current.getBoundingClientRect();
    const sx = e.clientX;
    const sy = e.clientY;
    let last: { x: number; y: number } | null = null;
    draggedRef.current = false;
    const move = (ev: PointerEvent) => {
      if (!draggedRef.current && Math.abs(ev.clientX - sx) + Math.abs(ev.clientY - sy) < 5) return;
      draggedRef.current = true;
      last = { x: r.left + ev.clientX - sx, y: r.top + ev.clientY - sy };
      setPos(last);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      if (!last) return;
      try {
        localStorage.setItem(POS_KEY, JSON.stringify(last));
      } catch {
        /* per-browser nicety only */
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  return (
    <div
      ref={boxRef}
      style={pos ? { left: pos.x, top: pos.y } : anchor ? { top: anchor.top, right: anchor.right } : undefined}
      className={cn('fixed z-[60]', !pos && !anchor && 'bottom-4 left-4')}
    >
      {open ? (
        <div
          style={!pos && anchor ? { maxHeight: window.innerHeight - anchor.top - 8 } : undefined}
          className="flex max-h-[calc(100vh-1rem)] w-[340px] max-w-[calc(100vw-1rem)] flex-col rounded-lg border border-amber-600/50 bg-card text-card-foreground shadow-lg">
          <div className="flex items-center gap-1 border-b px-2 py-1.5">
            <div
              onPointerDown={startDrag}
              title="Drag to move"
              className="flex min-w-0 flex-1 cursor-move touch-none select-none items-center gap-1.5 text-sm font-semibold"
            >
              <GripHorizontal className="h-4 w-4 shrink-0 text-muted-foreground" />
              <Headphones className="h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
              <span className="truncate">{form.name}</span>
            </div>
            {items.length > 1 && (
              <div className="flex items-center text-xs text-muted-foreground">
                <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Previous call" disabled={cur === 0} onClick={() => setIdx(cur - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="tabular-nums">
                  {cur + 1} of {items.length}
                </span>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  aria-label="Next call"
                  disabled={cur === items.length - 1}
                  onClick={() => setIdx(cur + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
            <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Minimise call audio feedback" onClick={() => setOpen(false)}>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>
          <div className="overflow-y-auto">
            {/* All cards stay mounted so a half-filled one keeps its answers while paging. */}
            {items.map((it, i) => (
              <div key={it.id} hidden={i !== cur}>
                <FeedbackCard item={it} form={form} onDone={() => done(it.id)} />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onPointerDown={startDrag}
          onClick={() => {
            const dragged = draggedRef.current;
            draggedRef.current = false;
            if (!dragged) setOpen(true);
          }}
          aria-label={`Call audio feedback, ${items.length} pending`}
          title="Call audio feedback form pending (optional). Click to fill, drag to move."
          className="relative flex h-10 w-10 touch-none select-none items-center justify-center rounded-full border-2 border-amber-600 bg-card text-amber-700 shadow-md dark:border-amber-400 dark:text-amber-400 transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {/* Soft glow so a pending form is noticed; off for reduced-motion users. */}
          <span aria-hidden className="pointer-events-none absolute inset-0 animate-ping rounded-full [animation-duration:2.5s] bg-amber-500/30 motion-reduce:hidden" />
          <ClipboardPenLine className="relative h-5 w-5" />
          <span className="absolute -right-1 -top-1 min-w-[18px] rounded-full bg-amber-700 px-1 text-center text-[10px] font-semibold leading-[18px] text-white">
            {items.length}
          </span>
        </button>
      )}
    </div>
  );
}
